import { useEffect, useMemo, useState } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Container from '@cloudscape-design/components/container'
import Form from '@cloudscape-design/components/form'
import FormField from '@cloudscape-design/components/form-field'
import Header from '@cloudscape-design/components/header'
import Input from '@cloudscape-design/components/input'
import Link from '@cloudscape-design/components/link'
import Modal from '@cloudscape-design/components/modal'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Spinner from '@cloudscape-design/components/spinner'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import { useApolloClient } from '@apollo/client/react'
import Page from '@/components/common/Page'
import {
  useAccount,
  useSetNotificationRecipients,
  useSendTestNotification,
  useRequestWhatsappVerification,
  useRequestTelegramConnection,
} from '@/hooks/useAccount'
import { GET_ACCOUNT } from '@/lib/operations/account'
import { notificationRecipientsSchema } from '@/lib/schemas'
import {
  MAX_RECIPIENTS_PER_CHANNEL,
  type NotificationRecipientGql,
  type NotificationRecipientInput,
  type TelegramConnectionInit,
  type WhatsappVerificationInit,
} from '@/lib/types'
import { useNotificationStore } from '@/state/notificationStore'

const formSchema = z.object({ recipients: notificationRecipientsSchema })

type RecipientFormRow = z.infer<typeof formSchema>['recipients'][number]

interface RecipientsForm {
  recipients: RecipientFormRow[]
}

function fromGql(r: NotificationRecipientGql): RecipientFormRow {
  return {
    id: r.id,
    channel: r.channel,
    label: r.label ?? undefined,
    phone: r.phone ?? undefined,
    chat_id: r.chatId ?? undefined,
    verified_at: r.verifiedAt ?? undefined,
    added_at: r.addedAt,
    persisted: true,
  }
}

function toInput(r: RecipientFormRow): NotificationRecipientInput {
  return {
    id: r.persisted ? r.id : undefined,
    channel: r.channel,
    label: r.label && r.label.trim().length > 0 ? r.label : undefined,
    phone: r.channel === 'whatsapp' ? r.phone : undefined,
    chatId: r.channel === 'telegram' ? r.chat_id : undefined,
  }
}

type RowStatus = 'draft' | 'unverified' | 'verified'

function rowStatus(row: RecipientFormRow | undefined, savedVerifiedAt: string | undefined): RowStatus {
  if (!row?.persisted) return 'draft'
  // Prefer the source-of-truth verifiedAt from Apollo data, falling back to the form state
  // so we never display "Unverified" after a webhook-driven refetch has updated the row.
  if (savedVerifiedAt || row.verified_at) return 'verified'
  return 'unverified'
}

interface WaModalState {
  open: boolean
  init?: WhatsappVerificationInit
  phone?: string
  recipientId?: string
}

interface TgModalState {
  open: boolean
  init?: TelegramConnectionInit
  // Telegram rows are created server-side on webhook. We snapshot the set of
  // already-verified telegram recipient ids when the modal opens so we can detect
  // a newly-verified row appearing in the refetch and auto-close.
  verifiedAtOpen?: Set<string>
}

export default function NotificationsTab() {
  const { account, loading: accountLoading } = useAccount()
  const { setRecipients, loading: saving } = useSetNotificationRecipients()
  const { sendTest, loading: testing } = useSendTestNotification()
  const { request: requestWa, loading: waLoading } = useRequestWhatsappVerification()
  const { request: requestTg, loading: tgLoading } = useRequestTelegramConnection()
  const apollo = useApolloClient()

  const saved = useMemo(() => account?.notificationRecipients ?? [], [account])

  const [waModal, setWaModal] = useState<WaModalState>({ open: false })
  const [tgModal, setTgModal] = useState<TgModalState>({ open: false })

  const { control, handleSubmit, watch, formState: { errors, isSubmitted }, reset } = useForm<RecipientsForm>({
    resolver: zodResolver(formSchema),
    defaultValues: { recipients: saved.map(fromGql) },
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'recipients' })

  // Reset the form only when the set of saved recipient ids changes (rows added
  // or removed server-side — e.g. a Telegram webhook creating a new recipient).
  // Verification status is read live from `savedById` so in-field edits aren't
  // clobbered by every background refetch.
  const savedIdsKey = saved.map((r) => r.id).sort().join(',')
  useEffect(() => {
    reset({ recipients: saved.map(fromGql) })
  }, [savedIdsKey, reset]) // eslint-disable-line react-hooks/exhaustive-deps

  const watched = watch('recipients')
  // Index the Apollo source of truth so each row's status reads live verification data
  // even if the react-hook-form state has not yet re-synced after refetch.
  const savedById = useMemo(() => {
    const map = new Map<string, NotificationRecipientGql>()
    saved.forEach((r) => map.set(r.id, r))
    return map
  }, [saved])

  const counts = { whatsapp: 0, telegram: 0 }
  watched?.forEach((r) => { if (r?.channel) counts[r.channel]++ })
  const whatsappAtCap = counts.whatsapp >= MAX_RECIPIENTS_PER_CHANNEL
  const telegramAtCap = counts.telegram >= MAX_RECIPIENTS_PER_CHANNEL

  const unverifiedCount = (watched ?? []).reduce((acc, row) => {
    const persistedSaved = row?.id ? savedById.get(row.id) : undefined
    return acc + (rowStatus(row, persistedSaved?.verifiedAt ?? undefined) === 'unverified' ? 1 : 0)
  }, 0)

  const handleAddWhatsapp = () => {
    append({
      id: `draft-${crypto.randomUUID()}`,
      channel: 'whatsapp',
      label: '',
      phone: '',
      chat_id: '',
      added_at: new Date().toISOString(),
      persisted: false,
    })
  }

  const handleSave = handleSubmit(async (data) => {
    try {
      await setRecipients(data.recipients.map(toInput))
      useNotificationStore.getState().add({ type: 'success', content: 'Notification recipients saved' })
    } catch (err) {
      useNotificationStore.getState().add({
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to save recipients',
      })
    }
  })

  const handleTest = async (recipientId: string) => {
    try {
      const result = await sendTest(recipientId)
      const out = result.data?.sendTestNotification
      if (out?.ok) {
        useNotificationStore.getState().add({ type: 'success', content: 'Test message sent' })
      } else {
        useNotificationStore.getState().add({ type: 'error', content: out?.error ?? 'Test send failed' })
      }
    } catch (err) {
      useNotificationStore.getState().add({
        type: 'error',
        content: err instanceof Error ? err.message : 'Test send failed',
      })
    }
  }

  const handleVerifyWa = async (row: RecipientFormRow) => {
    if (!row.persisted || !row.phone) {
      useNotificationStore.getState().add({
        type: 'warning',
        content: 'Save the recipient first, then request verification.',
      })
      return
    }
    try {
      const result = await requestWa(row.id)
      const init = result.data?.requestWhatsappVerification
      if (init) setWaModal({ open: true, init, phone: row.phone, recipientId: row.id })
    } catch (err) {
      useNotificationStore.getState().add({
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to request verification',
      })
    }
  }

  const handleConnectTelegram = async () => {
    try {
      const result = await requestTg()
      const init = result.data?.requestTelegramConnection
      const verifiedAtOpen = new Set(
        saved.filter((r) => r.channel === 'telegram' && r.verifiedAt).map((r) => r.id),
      )
      if (init) setTgModal({ open: true, init, verifiedAtOpen })
    } catch (err) {
      useNotificationStore.getState().add({
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to request Telegram connection',
      })
    }
  }

  const handleRefreshAfterVerify = () => {
    void apollo.refetchQueries({ include: [GET_ACCOUNT] })
  }

  // Auto-poll the account while a verify/connect modal is open so the user sees
  // the status flip to Verified without having to click anything.
  useEffect(() => {
    if (!waModal.open && !tgModal.open) return
    const interval = setInterval(() => {
      void apollo.refetchQueries({ include: [GET_ACCOUNT] })
    }, 2500)
    return () => clearInterval(interval)
  }, [waModal.open, tgModal.open, apollo])

  // Auto-close WhatsApp modal when the specific recipient becomes verified.
  useEffect(() => {
    if (!waModal.open || !waModal.recipientId) return
    const row = savedById.get(waModal.recipientId)
    if (row?.verifiedAt) {
      setWaModal({ open: false })
      useNotificationStore.getState().add({
        type: 'success',
        content: 'WhatsApp recipient verified — alerts will now reach this number.',
      })
    }
  }, [waModal.open, waModal.recipientId, savedById])

  // Auto-close Telegram modal when a newly-verified telegram recipient appears.
  useEffect(() => {
    if (!tgModal.open) return
    const baseline = tgModal.verifiedAtOpen ?? new Set<string>()
    const newlyVerified = saved.find(
      (r) => r.channel === 'telegram' && r.verifiedAt && !baseline.has(r.id),
    )
    if (newlyVerified) {
      setTgModal({ open: false })
      useNotificationStore.getState().add({
        type: 'success',
        content: 'Telegram recipient connected — alerts will now reach this chat.',
      })
    }
  }, [tgModal.open, tgModal.verifiedAtOpen, saved])

  // Only block on the initial load. Background refetches (e.g. our verification polling)
  // also flip `loading` true, but we must keep the current UI — including any open modal —
  // mounted so auto-close effects can fire and the user isn't jarred by a full re-render.
  if (accountLoading && !account) {
    return (
      <Page
        title="Notifications"
        helpContent={{
          header: 'Notification Settings',
          body: (
            <div>
              <p>Control how and when you receive alerts and notifications. Customize your notification preferences.</p>
              <h4>Options:</h4>
              <ul style={{ marginLeft: 20 }}>
                <li>Toggle notification types on/off</li>
                <li>Set delivery channels (email, in-app, etc.)</li>
                <li>Configure quiet hours</li>
                <li>Subscribe/unsubscribe from categories</li>
              </ul>
              <h4>Tips:</h4>
              <p>Disable notifications you don't need. Enable critical alerts so you never miss important events.</p>
            </div>
          ),
        }}
      >
        <Container>
          <Box textAlign="center" padding="l"><Spinner /></Box>
        </Container>
      </Page>
    )
  }

  const topLevelError = typeof errors.recipients?.message === 'string'
    ? errors.recipients.message
    : undefined

  return (
    <Page
      title="Notifications"
      helpContent={{
        header: 'Notification Settings',
        body: (
          <div>
            <p>Control how and when you receive alerts and notifications. Customize your notification preferences.</p>
            <h4>Options:</h4>
            <ul style={{ marginLeft: 20 }}>
              <li>Toggle notification types on/off</li>
              <li>Set delivery channels (email, in-app, etc.)</li>
              <li>Configure quiet hours</li>
              <li>Subscribe/unsubscribe from categories</li>
            </ul>
            <h4>Tips:</h4>
            <p>Disable notifications you don't need. Enable critical alerts so you never miss important events.</p>
          </div>
        ),
      }}
    >
      <Container
        header={
          <Header
            variant="h2"
            description={
              `Up to ${MAX_RECIPIENTS_PER_CHANNEL} recipients per channel. WhatsApp numbers must send a verification code before they can receive alerts. Telegram users connect by sending /start to our bot.`
            }
          >
            Notification Recipients
          </Header>
        }
      >
      <Form>
        <SpaceBetween size="l">
          {topLevelError && <Alert type="error">{topLevelError}</Alert>}

          {unverifiedCount > 0 && (
            <Alert type="warning" header="Action required">
              {unverifiedCount === 1
                ? '1 recipient is unverified and will not receive alerts. Click Verify on the row below to finish setup.'
                : `${unverifiedCount} recipients are unverified and will not receive alerts. Click Verify on each row below to finish setup.`}
            </Alert>
          )}

          {fields.length === 0 && (
            <Box color="text-body-secondary">
              No recipients yet. Use Add WhatsApp number or Connect Telegram below to get started.
            </Box>
          )}

          {fields.map((field, index) => {
            const row = watched?.[index]
            const channel = row?.channel ?? field.channel
            const rowErrors = errors.recipients?.[index]
            // Use row.id (form state) to look up the Apollo source of truth — field.id is
            // react-hook-form's synthetic key, NOT the recipient's real id.
            const persistedSaved = row?.id ? savedById.get(row.id) : undefined
            const status = rowStatus(row, persistedSaved?.verifiedAt ?? undefined)
            const isPersisted = row?.persisted === true
            const isWhatsapp = channel === 'whatsapp'

            return (
              <Container
                key={field.id}
                header={
                  <Header
                    variant="h3"
                    actions={
                      <SpaceBetween direction="horizontal" size="xs">
                        {status === 'draft' && (
                          <StatusIndicator type="info">Unsaved</StatusIndicator>
                        )}
                        {status === 'unverified' && (
                          <StatusIndicator type="warning">Unverified — cannot receive alerts</StatusIndicator>
                        )}
                        {status === 'verified' && (
                          <StatusIndicator type="success">Verified</StatusIndicator>
                        )}
                        <Button
                          variant="icon"
                          iconName="remove"
                          ariaLabel="Remove recipient"
                          onClick={() => remove(index)}
                        />
                      </SpaceBetween>
                    }
                  >
                    {isWhatsapp ? 'WhatsApp' : 'Telegram'}
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  <ColumnLayout columns={2}>
                    <FormField label="Label (optional)" errorText={rowErrors?.label?.message}>
                      <Controller
                        name={`recipients.${index}.label`}
                        control={control}
                        render={({ field: f }) => (
                          <Input
                            value={f.value ?? ''}
                            onChange={(e) => f.onChange(e.detail.value)}
                            onBlur={f.onBlur}
                            placeholder="e.g. On-call"
                          />
                        )}
                      />
                    </FormField>

                    {isWhatsapp && (
                      <FormField
                        label="Phone (E.164)"
                        errorText={rowErrors?.phone?.message}
                        description="e.g. +14155551234"
                      >
                        <Controller
                          name={`recipients.${index}.phone`}
                          control={control}
                          render={({ field: f }) => (
                            <Input
                              value={f.value ?? ''}
                              onChange={(e) => f.onChange(e.detail.value)}
                              onBlur={f.onBlur}
                              disabled={isPersisted}
                              placeholder="+14155551234"
                            />
                          )}
                        />
                      </FormField>
                    )}

                    {!isWhatsapp && persistedSaved?.chatId && (
                      <FormField label="Chat ID" description="Assigned by Telegram on /start">
                        <Box variant="samp">{persistedSaved.chatId}</Box>
                      </FormField>
                    )}
                  </ColumnLayout>

                  {!isWhatsapp && status === 'unverified' && (
                    <Alert type="warning">
                      This Telegram row is orphaned — the bot never attached a chat id to it. Remove it and use{' '}
                      <Box variant="strong">Connect Telegram</Box> in the Add recipient panel to reconnect.
                    </Alert>
                  )}

                  <SpaceBetween direction="horizontal" size="xs">
                    {isWhatsapp && status === 'unverified' && (
                      <Button
                        variant="primary"
                        disabled={!row?.phone || waLoading}
                        loading={waLoading}
                        onClick={() => row && handleVerifyWa(row)}
                      >
                        Verify WhatsApp
                      </Button>
                    )}
                    <Button
                      disabled={status !== 'verified' || testing}
                      loading={testing}
                      onClick={() => row?.id && handleTest(row.id)}
                    >
                      Send test
                    </Button>
                    {status === 'unverified' && isWhatsapp && (
                      <Box color="text-body-secondary" variant="small" padding={{ top: 'xs' }}>
                        Verify first to enable test sends.
                      </Box>
                    )}
                    {status === 'unverified' && !isWhatsapp && (
                      <Box color="text-body-secondary" variant="small" padding={{ top: 'xs' }}>
                        Remove and reconnect to enable test sends.
                      </Box>
                    )}
                    {status === 'draft' && (
                      <Box color="text-body-secondary" variant="small" padding={{ top: 'xs' }}>
                        Save this recipient to enable verification.
                      </Box>
                    )}
                  </SpaceBetween>
                </SpaceBetween>
              </Container>
            )
          })}

          <Container
            header={<Header variant="h3" description="Add a new recipient. WhatsApp needs a phone number; Telegram connects via a bot deep-link.">Add recipient</Header>}
          >
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                iconName="add-plus"
                disabled={whatsappAtCap}
                onClick={handleAddWhatsapp}
              >
                Add WhatsApp number
              </Button>
              <Button
                iconName="external"
                disabled={telegramAtCap || tgLoading}
                loading={tgLoading}
                onClick={handleConnectTelegram}
              >
                Connect Telegram
              </Button>
            </SpaceBetween>
          </Container>

          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="primary" loading={saving} onClick={() => { void handleSave() }}>
              Save
            </Button>
            {isSubmitted && errors.recipients && (
              <Box color="text-status-error" variant="small">
                Fix the errors above before saving.
              </Box>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Form>

      <Modal
        visible={waModal.open}
        header="Verify WhatsApp"
        onDismiss={() => { setWaModal({ open: false }); handleRefreshAfterVerify() }}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => { setWaModal({ open: false }); handleRefreshAfterVerify() }}>Close</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box>
            <strong>Step 1:</strong> From the phone <strong>{waModal.phone}</strong>, open WhatsApp and send this exact
            message to <strong>{waModal.init?.phoneNumber}</strong>:
          </Box>
          <Box fontSize="heading-m" fontWeight="bold">
            Verify {waModal.init?.code}
          </Box>
          {waModal.init?.waLink && (
            <Link external href={waModal.init.waLink}>
              Tap to open WhatsApp with this message pre-filled
            </Link>
          )}
          <Box>
            <strong>Step 2:</strong> This page is checking for confirmation automatically.
          </Box>
          <Box color="text-status-info" variant="small">
            <Spinner size="normal" /> Waiting for your message… this modal will close when verification succeeds.
          </Box>
          <Alert type="info">
            Code expires in 10 minutes. If nothing happens after you've sent the message, confirm your Twilio WhatsApp
            sender is connected to this phone number (sandbox users must first send <Box variant="samp">join &lt;your
            sandbox code&gt;</Box>).
          </Alert>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={tgModal.open}
        header="Connect Telegram"
        onDismiss={() => { setTgModal({ open: false }); handleRefreshAfterVerify() }}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => { setTgModal({ open: false }); handleRefreshAfterVerify() }}>Close</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box><strong>Step 1:</strong> Open the Telegram bot and send the start message:</Box>
          {tgModal.init?.botUrl && (
            <Link external href={tgModal.init.botUrl}>
              Open @{tgModal.init.botUsername}
            </Link>
          )}
          <Box>
            Or manually send this message to @{tgModal.init?.botUsername}:
          </Box>
          <Box fontSize="heading-m" fontWeight="bold">
            /start {tgModal.init?.code}
          </Box>
          <Box>
            <strong>Step 2:</strong> This page is checking for confirmation automatically.
          </Box>
          <Box color="text-status-info" variant="small">
            <Spinner size="normal" /> Waiting for the bot… this modal will close when verification succeeds.
          </Box>
          <Alert type="info">
            Code expires in 10 minutes. Your Telegram chat is added automatically once the bot receives the message.
          </Alert>
        </SpaceBetween>
      </Modal>
      </Container>
    </Page>
  )
}
